import React from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Snackbar, Typography } from '@mui/material';
import { BotOpsHttpError } from './api/botOpsClient';
import { postBulkDecisions } from './api/botOpsService';
import { FONT, RADIUS } from './lib/brand';

export const UNDO_MS = 10_000;

const newKey = () => {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c && c.randomUUID ? c.randomUUID() : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const failText = (e: unknown): string => {
  if (e instanceof BotOpsHttpError) {
    if (e.status === 429) return `Too many changes. Try again in ${e.retryAfter || 30} s.`;
    if (e.status === 403) return 'Only the super admin can make changes here.';
    return e.message;
  }
  return (e as Error)?.message || 'Something went wrong';
};

interface Undoable { ids: string[]; value: 'yes' | 'no'; skipped: number }

/**
 * Approve or decline several pending items at once (DASHBOARD.md §1.18).
 * A confirmation comes first; afterwards a toast offers Undo for 10 s, which
 * writes `pending` for every item that was changed (append-only: nothing is
 * deleted). Items the Implementation Bot has already started are reported, not
 * touched. Bulk Yes never posts to Chat; that needs the single-item preview.
 */
export function BulkBar({ selected, onClear, refresh }: { selected: string[]; onClear: () => void; refresh: () => Promise<void> | void }) {
  const [confirm, setConfirm] = React.useState<'yes' | 'no' | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [undo, setUndo] = React.useState<Undoable | null>(null);
  const timer = React.useRef<number | null>(null);
  const keyRef = React.useRef<{ k: string; value: string; ids: string } | null>(null);
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; if (timer.current) window.clearTimeout(timer.current); }; }, []);

  const apply = async (value: 'yes' | 'no') => {
    if (busy) return;
    const ids = [...selected];
    const sig = ids.slice().sort().join(',');
    // A retry after a failure of the same request reuses its key, so a lost response cannot write twice.
    if (!keyRef.current || keyRef.current.value !== value || keyRef.current.ids !== sig) keyRef.current = { k: newKey(), value, ids: sig };
    setBusy(true); setError(null);
    try {
      const r = await postBulkDecisions({ items: ids, value, idempotencyKey: keyRef.current.k });
      keyRef.current = null;
      const results = (r.data && r.data.results) || [];
      const changed = results.filter((x) => x.ok && !x.noop).map((x) => x.action_id);
      const started = results.filter((x) => x.already_in_progress).length;
      const failed = results.filter((x) => !x.ok && !x.already_in_progress).length;
      await refresh();
      if (!alive.current) return;
      setConfirm(null);
      onClear();
      const parts = [`${changed.length} ${value === 'yes' ? 'approved' : 'declined'}`];
      if (started) parts.push(`${started} already in progress`);
      if (failed) parts.push(`${failed} could not be saved`);
      setNotice(parts.join(' · '));
      if (changed.length) {
        setUndo({ ids: changed, value, skipped: started + failed });
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => { if (alive.current) setUndo(null); }, UNDO_MS);
      }
    } catch (e) {
      if (alive.current) setError(failText(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const doUndo = async () => {
    if (!undo) return;
    const u = undo;
    setUndo(null);
    if (timer.current) window.clearTimeout(timer.current);
    try {
      await postBulkDecisions({ items: u.ids, value: 'pending', idempotencyKey: newKey() });
      await refresh();
      if (alive.current) setNotice(`Undone: ${u.ids.length} back to pending`);
    } catch (e) {
      if (alive.current) setNotice(`Could not undo: ${failText(e)}`);
    }
  };

  const n = selected.length;
  return (
    <>
      {n > 0 && (
        <Paper elevation={6} data-testid="bulk-bar" role="region" aria-label="Bulk decisions" sx={{ position: 'sticky', bottom: 12, zIndex: 3, mx: 2, mb: 1.5, mt: 1, px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap', borderRadius: RADIUS.lg }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{n} selected</Typography>
          <Button size="small" variant="contained" color="success" onClick={() => setConfirm('yes')} sx={{ textTransform: 'none' }}>Approve selected</Button>
          <Button size="small" variant="outlined" color="inherit" onClick={() => setConfirm('no')} sx={{ textTransform: 'none' }}>Decline selected</Button>
          <Button size="small" onClick={onClear} sx={{ textTransform: 'none' }}>Clear</Button>
        </Paper>
      )}

      <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} aria-labelledby="bulk-title" data-testid="bulk-confirm">
        <DialogTitle id="bulk-title" sx={{ fontFamily: FONT.heading, fontSize: 16, fontWeight: 700 }}>
          {confirm === 'yes' ? `Approve ${n} item${n === 1 ? '' : 's'} and queue ${n === 1 ? 'it' : 'them'} for the Implementation Bot?` : `Decline ${n} item${n === 1 ? '' : 's'}?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            One decision is recorded for each item and sent to Grok Bot. You can undo for 10 seconds afterwards.{confirm === 'yes' ? ' Approving in bulk never posts to Google Chat.' : ''}
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 1.5 }} role="alert">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={confirm === 'yes' ? 'success' : 'inherit'} disabled={busy} onClick={() => confirm && apply(confirm)} autoFocus>
            {error ? 'Retry' : confirm === 'yes' ? 'Confirm approve' : 'Confirm decline'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(undo)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" data-testid="undo-toast" action={<Button color="inherit" size="small" onClick={doUndo}>Undo (10 s)</Button>}>
          {notice}
        </Alert>
      </Snackbar>
      <Snackbar open={Boolean(notice) && !undo} autoHideDuration={5000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" variant="filled" onClose={() => setNotice(null)}>{notice}</Alert>
      </Snackbar>
    </>
  );
}

/** A row can join a bulk action only while it is a pending, decidable proposal. */
export const isSelectable = (a: { decidable: boolean; decision?: { value?: string | null } | null }): boolean =>
  a.decidable && (!a.decision || !a.decision.value || a.decision.value === 'pending');
